/**
 * Inline-emphasis preservation for Refine Wording (S1-RW-06).
 *
 * The refine service takes and returns PLAIN text and — confirmed empirically —
 * strips any markdown we send, so emphasis can't be round-tripped through it.
 * Instead we capture the emphasised phrases (bold / italic / underline) from the
 * selection BEFORE refining, then re-apply those marks to the same phrases where
 * they survive verbatim in the refined text. A term like "acknowledge" that the
 * reword keeps stays bold; emphasis on a word the model actually rewrote is lost
 * (unrecoverable once the text changes — the honest limit of a plain-text service).
 *
 * Font / size / colour are handled separately via the destination's donor marks;
 * this module only concerns the emphasis marks.
 */
import {
  type Mark,
  type MarkType,
  type Node as PMNode,
  type Schema,
} from "prosemirror-model";

const BOLD = ["bold", "strong"];
const ITALIC = ["italic", "em"];
const UNDERLINE = ["underline"];

/** Emphasis mark names — also used to strip emphasis from the donor marks so it
 *  isn't smeared uniformly across the refined text. */
export const EMPHASIS_MARK_NAMES = new Set<string>([
  ...BOLD,
  ...ITALIC,
  ...UNDERLINE,
]);

/** An emphasised span captured from the original selection. */
export type EmphasisPhrase = { text: string; markNames: string[] };

// Short phrases match too eagerly (a bold "is" would re-bold every "is"), so we
// only re-apply phrases of at least this trimmed length.
const MIN_PHRASE_LENGTH = 3;

/**
 * Collect the emphasised spans in `[from, to]`. Adjacent runs sharing the same
 * emphasis set are merged (so "I acknowledge" is one phrase, not two), block
 * boundaries break a run, and phrases shorter than `MIN_PHRASE_LENGTH` (after
 * trimming) are dropped to avoid spurious matches.
 */
export const collectEmphasisPhrases = (
  doc: PMNode,
  from: number,
  to: number
): EmphasisPhrase[] => {
  type Run = { text: string; key: string; names: string[] };
  const runs: Run[] = [];
  let current: Run | null = null;
  const breakRun = () => {
    if (current) runs.push(current);
    current = null;
  };

  doc.nodesBetween(from, to, (node, pos) => {
    if (node.isText) {
      const start = Math.max(from, pos);
      const end = Math.min(to, pos + node.nodeSize);
      if (end <= start) return false;
      const names = node.marks
        .map((m) => m.type.name)
        .filter((n) => EMPHASIS_MARK_NAMES.has(n))
        .sort();
      const key = names.join(",");
      const text = (node.text ?? "").slice(start - pos, end - pos);
      if (!key) {
        breakRun(); // un-emphasised run ends any open phrase
      } else if (current && current.key === key) {
        current.text += text;
      } else {
        breakRun();
        current = { text, key, names };
      }
      return false;
    }
    // Block boundary / hardBreak / atom — ends any open phrase.
    breakRun();
    return true;
  });
  breakRun();

  const seen = new Set<string>();
  const out: EmphasisPhrase[] = [];
  for (const run of runs) {
    const text = run.text.trim();
    if (text.length < MIN_PHRASE_LENGTH) continue;
    const dedupeKey = `${text} ${run.key}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    out.push({ text, markNames: run.names });
  }
  return out;
};

/**
 * Build inline nodes for `text`, layering `baseMarks` on every run and
 * additionally re-applying each phrase's emphasis marks to every verbatim
 * occurrence of that phrase. `\n` becomes a hardBreak. When `phrases` is empty
 * this is just `text` with `baseMarks` (the plain path).
 */
export const applyEmphasisPhrases = (
  schema: Schema,
  text: string,
  baseMarks: readonly Mark[],
  phrases: readonly EmphasisPhrase[]
): PMNode[] => {
  const n = text.length;
  // Per-character set of emphasis mark names to apply.
  const perChar: Set<string>[] = Array.from({ length: n }, () => new Set<string>());
  for (const phrase of phrases) {
    if (!phrase.text) continue;
    let idx = text.indexOf(phrase.text);
    while (idx !== -1) {
      for (let i = idx; i < idx + phrase.text.length; i++) {
        for (const m of phrase.markNames) perChar[i].add(m);
      }
      idx = text.indexOf(phrase.text, idx + phrase.text.length);
    }
  }

  const typeCache: Record<string, MarkType | null> = {};
  const getType = (name: string): MarkType | null => {
    if (!(name in typeCache)) typeCache[name] = schema.marks[name] ?? null;
    return typeCache[name];
  };
  const hardBreakType = schema.nodes.hardBreak;

  const out: PMNode[] = [];
  let buf = "";
  let curKey = "";
  const flush = () => {
    if (!buf) return;
    let marks: readonly Mark[] = baseMarks;
    if (curKey) {
      for (const name of curKey.split(",")) {
        const type = getType(name);
        if (type) marks = type.create().addToSet(marks);
      }
    }
    out.push(schema.text(buf, marks.length ? marks : undefined));
    buf = "";
  };

  for (let i = 0; i < n; i++) {
    const ch = text[i];
    if (ch === "\n") {
      flush();
      if (hardBreakType) out.push(hardBreakType.create());
      else buf += " ";
      curKey = "";
      continue;
    }
    const key = Array.from(perChar[i]).sort().join(",");
    if (key !== curKey) {
      flush();
      curKey = key;
    }
    buf += ch;
  }
  flush();
  return out;
};
